"use strict";
/**
 * 「直接在页面上点一段来编辑」的消息协议（控制台 ↔ iframe 里的预览页）。
 *
 * ## 为什么需要
 *
 * 左侧表单 + 右侧预览的布局有个绕不开的限制：客户要在**上百个输入框**里
 * 找到"页面上那一句话"对应的那个框。字段名是 `title` 还是 `heading`、
 * 是 `items[2].label` 还是 `links[2].text`，他既不知道也不该知道。
 * 他真正想说的是"我要改**这一句**"——指哪改哪，比任何导航都直接。
 *
 * 所以方向反过来：**页面是主语**。点哪一段，就弹出那一段的字段。
 *
 * ## 为什么还是要 postMessage
 *
 * 控制台和客户站跨站，控制台读不到 iframe 的 DOM，也就无法自己判断"点在了哪一段"。
 * 只有页面自己知道自己的结构，所以点选必须由页面完成、再报给控制台。
 *
 * ## 两种 id 都要带
 *
 * 版式里的格子挂 `data-instance`（主题实例 id），客户自己加的块挂 `data-block-id`
 * （区块 id）。控制台两份 id 都要能对上块，只带一种就有一类块点不出来。
 *
 * ## 点选默认关闭
 *
 * 预览页里点击本来是**导航**（点导航栏、点卡片跳详情）。预览时的第一条诉求
 * 恰恰是"这样改完在页面上看起来对不对"，所以不能一进预览就把链接全废掉。
 * 开关由控制台显式发：进入全屏编辑才发 `enabled: true`，退出就发 `false`。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PICK_TYPE = exports.PICK_SOURCE = exports.PICK_MODE_TYPE = exports.PICK_MODE_SOURCE = void 0;
exports.buildPickModeMessage = buildPickModeMessage;
exports.readPickModeCommand = readPickModeCommand;
exports.buildPickMessage = buildPickMessage;
exports.readBlockPick = readBlockPick;
/** 控制台 → 预览页：开/关点选。与高亮协议共用 `icetus-console` 这个来源标记 */
exports.PICK_MODE_SOURCE = 'icetus-console';
exports.PICK_MODE_TYPE = 'icetus:block-pick-mode';
/** 预览页 → 控制台：客户点了某一段 */
exports.PICK_SOURCE = 'icetus-preview';
exports.PICK_TYPE = 'icetus:block-pick';
/** id 上限。和 `preview-highlight` 保持同一个量级，别让一条消息带上整段正文 */
const MAX_ID = 128;
/** 组件名（`data-component`）只用来在认不出块时给一句人话，短就够 */
const MAX_COMPONENT = 40;
function buildPickModeMessage(enabled) {
    return { source: exports.PICK_MODE_SOURCE, type: exports.PICK_MODE_TYPE, enabled };
}
function isRecord(value) {
    return typeof value === 'object' && value !== null;
}
function cleanId(value) {
    if (typeof value !== 'string')
        return null;
    const id = value.trim();
    if (id.length === 0 || id.length > MAX_ID)
        return null;
    return id;
}
/**
 * 解析一条发给预览页的开关消息。`null` = 不是本协议，调用方直接忽略。
 *
 * 与高亮协议一样，`source` 不是安全边界（谁都能伪造），只是廉价过滤：
 * 页面上可能同时有客服脚本、表单嵌入在发 postMessage。
 */
function readPickModeCommand(data) {
    if (!isRecord(data))
        return null;
    if (data['source'] !== exports.PICK_MODE_SOURCE)
        return null;
    if (data['type'] !== exports.PICK_MODE_TYPE)
        return null;
    return data['enabled'] === true;
}
function buildPickMessage(pick) {
    return {
        source: exports.PICK_SOURCE,
        type: exports.PICK_TYPE,
        instanceId: pick.instanceId,
        blockId: pick.blockId,
        component: pick.component.slice(0, MAX_COMPONENT),
    };
}
/**
 * 解析一条**发给控制台**的点选消息。
 *
 * 两个 id 都空的消息一律当无效：页面里点到了版式之外的地方（比如页脚版权行），
 * 报上来一个空目标，控制台只能猜 —— 不如什么都不做。
 */
function readBlockPick(data) {
    if (!isRecord(data))
        return null;
    if (data['source'] !== exports.PICK_SOURCE)
        return null;
    if (data['type'] !== exports.PICK_TYPE)
        return null;
    const instanceId = cleanId(data['instanceId']);
    const blockId = cleanId(data['blockId']);
    if (!instanceId && !blockId)
        return null;
    const component = typeof data['component'] === 'string' ? data['component'].trim().slice(0, MAX_COMPONENT) : '';
    return { instanceId, blockId, component };
}
//# sourceMappingURL=preview-pick.js.map